<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" :id="`${testName}-test`">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <div>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          class="grid-container"
          id="simple-test-grid"
        >
          <div
            v-if="this.testId === 'prader_willi'"
            style="grid-area: testInfo"
          >
            <p>
              <a href="https://www.vcgs.org.au/order/tests/657" target="_blank">
                Prader-Willi syndrome</a
              >
              (PWS) is characterised by severe hypotonia and feeding
              difficulties in early infancy, followed in later infancy or early
              childhood by excessive eating and gradual development of morbid
              obesity (unless eating is externally controlled). All individuals
              have some degree of cognitive impairment. Prader-willi has
              multiple causes, all affecting chromosome region 15q11.1-q11.3.
            </p>
            <p>
              <a href="https://www.vcgs.org.au/order/tests/570" target="_blank">
                Angelman syndrome</a
              >
              (AS) is characterised by severe developmental delay or
              intellectual disability, severe speech impairment, gait ataxia and
              a unique behaviour with an overly happy demeanour that includes
              frequent laughing, smiling, and excitability. Developmental delays
              are first noted at around age six months; however, the clinical
              features of AS do not become apparent until after age one year.
              Angelman syndrome has multiple causes, all affecting chromosome
              region 15q11.1-q11.3.
            </p>
          </div>
          <p v-if="this.testId === 'fragilex'" style="grid-area: testInfo">
            <a href="https://www.vcgs.org.au/order/tests/610" target="_blank">
              Fragile X syndrome
            </a>
            (FXS) is the most common cause of inherited intellectual disability.
            People with FXS can have developmental delay, learning difficulties,
            anxiety, autism and epilepsy. The features of FXS vary from mild to
            severe with males more likely to be severely affected than females
            because the gene is found on the X-chromosome.
          </p>
          <v-checkbox
            style="grid-area: urgentKit"
            v-model="urgentKit"
            :label="`Urgent kit required`"
            :id="testId + '.urgent'"
            class="mb-3"
            v-if="patientdata.sampleType === 'SALIVA'"
          ></v-checkbox>
          <v-row v-if="urgentKit">
            <v-col class="custom-col">
              <span class="text-title">Urgent kit reason</span>
              <v-text-field
                v-model="urgentReason"
                :id="testId + '.urgentReason'"
                outlined
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="my-2">
            <v-col cols="12">
              <span class="text-title">Is the patient pregnant?*</span>
              <v-select
                v-model="pregnant"     
                :items="pregnancyOptions"     
                item-text="key"               
                item-value="value"             
                :id="testId + '.pregnant'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. clinical indication for testing, relevant family history,
                results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>

          <TestPriceAlert :price="price" :medicare="medicare" />
        </v-form>
      </div>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        :id="testId + '.next_section'"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";
import { computed, watch } from 'vue';  

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  mixins: [FormRulesMixin, RetrieveJSONMixin],
  components: { TestPriceAlert },
  name: "",
  data: () => ({
    urgent: false,
    simpleTestPricing: null,
    urgentKit: false,
    urgentReason: "",
    clinicalNotes: "",
    pregnancyOptions: [
      { key: "Yes", value: "YES" },
      { key: "No", value: "NO" }
    ],
  }),
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.simpleTestPricing = await this.execRetrieveJson(
      "json/pricing/simple.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById(this.testName + "-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
  },
  computed: {
    getPriceDetails() {
      if (!this.simpleTestPricing) return null;
      return this.simpleTestPricing[this.testId];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails !== null) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },
  setup() {
    const store = useSectionStore()
    const pregnant = computed({
      get: () => store.pregnant,
      set: val => { store.pregnant = val },
    })
    watch(pregnant, val => { store.pregnant = val })
    return { pregnant }
  },
  methods: {
    transferData: function() {
      return {
        testType: this.testId,
        urgent: this.urgentKit,
        urgentReason: this.urgentReason,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
      };
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
  },
};
</script>
<style scoped></style>
